import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import axios from 'axios';
import { House, Warehouse, Calculator, Desktop, Package, Truck, Leaf, ShoppingCart, Storefront, ListChecks, UserGear, SignOut, ArrowUUpLeft, ChartBar, ChatCircleDots, ClipboardText, Users, List, X, Buildings, Pulse, EnvelopeSimple, Archive, Tag, IdentificationCard, WarningCircle, Kanban, ShieldCheck, CalendarBlank, Barcode, Receipt, Sparkle, Microphone, FileMagnifyingGlass } from '@phosphor-icons/react';

const MODULE_KEYS = {
  '/': 'dashboard',
  '/dept/warehouse': 'warehouse',
  '/dept/accounts': 'accounts',
  '/dept/edp': 'edp',
  '/dept/packing': 'packing',
  '/dept/logistics': 'logistics',
  '/dept/raw_materials': 'raw_materials',
  '/dept/ecommerce': 'ecommerce',
  '/dept/branch_activities': 'branch_activities',
  '/company-module': 'company_module',
  '/stock-check': 'stock_check',
  '/reports': 'reports',
  '/staff-attendance': 'staff_attendance',
  '/chat': 'chat',
  '/todo': 'todo',
  '/store-opening': 'store_opening',
  '/purchase-rate': 'purchase_rate',
  '/customer-frequency': 'customer_frequency',
  '/address-print': 'address_print',
  '/store-requests': 'store_requests',
  '/workflow-tasks': 'workflow_tasks',
  '/workflow-approvals': 'workflow_approvals',
  '/workflow-meetings': 'workflow_meetings',
  '/barcode-verify': 'barcode_verify',
  '/sales-gst-report': 'sales_gst_report',
  '/b2c-sales': 'b2c_sales',
  '/sales-purchase-compare': 'sales_purchase_compare',
  '/distribution-hub': 'distribution_hub',
  '/ai-helper': 'ai_helper',
  '/branch-cash': 'branch_cash',
  '/voice-notes': 'voice_notes',
  '/bill-analyzer': 'bill_margin_analyzer',
};

export default function Sidebar({ user, permissions, onLogout, isImpersonating }) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadMentions, setUnreadMentions] = useState(0);
  const [wfBadges, setWfBadges] = useState({ tasks: 0, approvals: 0, meetings: 0 });

  useEffect(() => {
    if (!user) return;
    const API = process.env.REACT_APP_BACKEND_URL + '/api';
    const headers = { Authorization: `Bearer ${localStorage.getItem('crm_token')}` };
    const fetchUnread = async () => {
      try {
        const r = await axios.get(`${API}/chat/mentions/unread`, { headers });
        setUnreadMentions(r.data?.total || 0);
      } catch {}
      try {
        const b = await axios.get(`${API}/workflow/badges`, { headers });
        setWfBadges(b.data || { tasks: 0, approvals: 0, meetings: 0 });
      } catch {}
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [user, location.pathname]);

  const allItems = [
    { path: '/', icon: House, label: 'Dashboard', testId: 'nav-dashboard' },
    { path: '/dept/warehouse', icon: Warehouse, label: 'Warehouse', testId: 'nav-warehouse' },
    { path: '/dept/accounts', icon: Calculator, label: 'Accounts', testId: 'nav-accounts' },
    { path: '/company-module', icon: Buildings, label: 'Company Module', testId: 'nav-company-module' },
    { path: '/dept/edp', icon: Desktop, label: 'EDP', testId: 'nav-edp' },
    { path: '/dept/packing', icon: Package, label: 'Packing', testId: 'nav-packing' },
    { path: '/dept/logistics', icon: Truck, label: 'Logistics', testId: 'nav-logistics' },
    { path: '/dept/raw_materials', icon: Leaf, label: 'Raw Materials', testId: 'nav-raw-materials' },
    { path: '/dept/ecommerce', icon: ShoppingCart, label: 'E-Commerce', testId: 'nav-ecommerce' },
    { path: '/dept/branch_activities', icon: Storefront, label: 'Branch Activities', testId: 'nav-branch-activities' },
    { path: '/stock-check', icon: ClipboardText, label: 'Stock Check', testId: 'nav-stock-check' },
    { path: '/staff-attendance', icon: Users, label: 'Staff Attendance', testId: 'nav-staff-attendance' },
    { path: '/store-opening', icon: Archive, label: 'Store Opening', testId: 'nav-store-opening' },
    { path: '/purchase-rate', icon: Tag, label: 'Purchase Rate', testId: 'nav-purchase-rate' },
    { path: '/customer-frequency', icon: IdentificationCard, label: 'Customer Frequency', testId: 'nav-customer-frequency' },
    { path: '/address-print', icon: EnvelopeSimple, label: 'Address Print', testId: 'nav-address-print' },
    { path: '/store-requests', icon: WarningCircle, label: 'Store Requests', testId: 'nav-store-requests' },
    { path: '/workflow-tasks', icon: Kanban, label: 'Workflow Tasks', testId: 'nav-workflow-tasks' },
    { path: '/workflow-approvals', icon: ShieldCheck, label: 'Approvals', testId: 'nav-workflow-approvals' },
    { path: '/workflow-meetings', icon: CalendarBlank, label: 'Meetings', testId: 'nav-workflow-meetings' },
    { path: '/barcode-verify', icon: Barcode, label: 'Barcode Verify', testId: 'nav-barcode-verify' },
    { path: '/sales-gst-report', icon: Receipt, label: 'GST B2B Sales', testId: 'nav-sales-gst-report' },
    { path: '/b2c-sales', icon: Storefront, label: 'B2C Sales', testId: 'nav-b2c-sales' },
    { path: '/sales-purchase-compare', icon: ChartBar, label: 'Sales vs Purchase', testId: 'nav-sales-purchase-compare' },
    { path: '/distribution-hub', icon: Sparkle, label: 'Distribution Hub', testId: 'nav-distribution-hub' },
    { path: '/ai-helper', icon: ChatCircleDots, label: 'Saran (AI Helper)', testId: 'nav-ai-helper' },
    { path: '/branch-cash', icon: Calculator, label: 'Branch Cash & Expenses', testId: 'nav-branch-cash' },
    { path: '/voice-notes', icon: Microphone, label: 'Voice Notes', testId: 'nav-voice-notes' },
    { path: '/bill-analyzer', icon: FileMagnifyingGlass, label: 'Bill Margin Analyzer', testId: 'nav-bill-analyzer' },
    { path: '/reports', icon: ChartBar, label: 'Reports', testId: 'nav-reports' },
    { path: '/chat', icon: ChatCircleDots, label: 'Chat', testId: 'nav-chat' },
    { path: '/todo', icon: ListChecks, label: 'To Do Task', testId: 'nav-todo' },
  ];

  const isAdmin = user.role === 'admin';
  const navItems = allItems.filter(item => {
    if (isAdmin) return true;
    const moduleKey = MODULE_KEYS[item.path];
    if (!moduleKey) return true;
    if (moduleKey === 'dashboard') return true;
    // Backwards-compat: Company Module used to be gated by `accounts`. Fall back if the newer
    // `company_module` key isn't explicitly set on the user yet.
    if (moduleKey === 'company_module' && permissions?.company_module === undefined) {
      return permissions?.accounts?.view === true;
    }
    return permissions?.[moduleKey]?.view === true;
  });

  if (isAdmin) {
    navItems.push({ path: '/users', icon: UserGear, label: 'Users', testId: 'nav-users' });
    navItems.push({ path: '/user-activity', icon: Pulse, label: 'User Activity', testId: 'nav-user-activity' });
    navItems.push({ path: '/email-schedules', icon: EnvelopeSimple, label: 'Email Schedules', testId: 'nav-email-schedules' });
  }

  const sidebarContent = (
    <>
      <div className="px-5 pt-6 pb-5 border-b border-white/10 flex items-start justify-between">
        <div>
          <h2 className="brand-mark text-3xl text-white leading-none">SAMUDRIKA</h2>
          <p className="label-eyebrow mt-2 text-white/50">Silver &amp; Jewellery CRM</p>
        </div>
        <button onClick={() => setMobileOpen(false)} className="lg:hidden p-1 hover:bg-white/10 rounded" data-testid="sidebar-close">
          <X size={20} className="text-white/70" />
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.path === '/' 
            ? location.pathname === '/'
            : location.pathname.startsWith(item.path);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={item.testId}
              onClick={() => setMobileOpen(false)}
              className={`group flex items-center gap-3 px-3 py-2.5 rounded-md mb-0.5 transition-all duration-150 text-sm relative ${
                isActive 
                  ? 'bg-white/[0.06] text-white' 
                  : 'text-white/60 hover:bg-white/[0.04] hover:text-white'
              }`}
            >
              {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-[#C9A961] rounded-r" />}
              <Icon size={17} weight={isActive ? 'fill' : 'regular'} className={isActive ? 'text-[#C9A961]' : ''} />
              <span className={`font-medium flex-1 ${isActive ? 'text-white' : ''}`}>{item.label}</span>
              {item.path === '/chat' && unreadMentions > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#C9A961] text-[#0A0A0A]" data-testid="sidebar-chat-badge">{unreadMentions}</span>
              )}
              {item.path === '/workflow-tasks' && wfBadges.tasks > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white" data-testid="sidebar-tasks-badge">{wfBadges.tasks}</span>
              )}
              {item.path === '/workflow-approvals' && wfBadges.approvals > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white" data-testid="sidebar-approvals-badge">{wfBadges.approvals}</span>
              )}
              {item.path === '/workflow-meetings' && wfBadges.meetings > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white" data-testid="sidebar-meetings-badge">{wfBadges.meetings}</span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-3 pb-4 border-t border-white/10 pt-3">
        <div className="px-3 py-2 mb-2">
          <p className="text-xs text-white/80 font-medium leading-tight">{user.name}</p>
          <p className="label-eyebrow text-white/40 mt-0.5" style={{ fontSize: '0.6rem' }}>{user.role.replace('_', ' ')}</p>
        </div>
        <button
          onClick={() => { setMobileOpen(false); onLogout(); }}
          data-testid="logout-button"
          className={`flex items-center gap-3 px-3 py-2 rounded-md w-full transition-all duration-150 text-sm ${
            isImpersonating 
              ? 'bg-[#8B5CF6] text-white hover:bg-[#7C3AED]'
              : 'text-white/70 hover:bg-white/5 hover:text-white'
          }`}
        >
          {isImpersonating ? <ArrowUUpLeft size={18} /> : <SignOut size={18} />}
          <span className="font-medium">{isImpersonating ? 'Return to Admin' : 'Logout'}</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger button - fixed top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#0A0A0A] px-4 py-3 flex items-center justify-between border-b border-white/10" data-testid="mobile-header">
        <button onClick={() => setMobileOpen(true)} className="text-white p-1" data-testid="sidebar-toggle">
          <List size={24} />
        </button>
        <h2 className="text-white brand-mark text-xl tracking-wider">SAMUDRIKA</h2>
        <p className="text-white/60 text-xs">{user.name}</p>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/60" onClick={() => setMobileOpen(false)} data-testid="sidebar-overlay" />
      )}

      {/* Sidebar - desktop: static, mobile: overlay */}
      <div className={`
        fixed lg:static top-0 left-0 z-50 h-screen
        w-64 bg-[#0A0A0A] text-white flex flex-col
        transition-transform duration-200 ease-in-out
        border-r border-black
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} data-testid="sidebar">
        {sidebarContent}
      </div>
    </>
  );
}
